import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Trip = {
  uuid: string; destination: string; departure_at: string; return_at: string | null;
  carrier: string | null; reference: string | null; status: string; notes: string | null; ledger_seq: number;
  segment_count: number; accommodation_count: number; companion_count: number; minor_count: number;
};
type Segment = { uuid: string; seq: number; from_place: string | null; to_country: string | null; to_point: string | null; carrier: string | null; reference: string | null; travel_at: string | null };
type Accom = { uuid: string; seq: number; name: string | null; address: string | null; from_date: string | null; to_date: string | null };
type Companion = { uuid: string; name: string; relationship: string | null; is_minor: number; dob: string | null };
type TripDetail = Trip & { segments: Segment[]; accommodation: Accom[]; companions: Companion[] };

const fmt = (t: string) => t.replace('T', ' ').replace(/:\d\d\.\d+Z$/, '').replace(/Z$/, '');
const daysUntil = (iso: string) => Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);

export function Travel() {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  async function load() {
    try { setTrips((await fetch('/api/trips').then((r) => r.json())).trips ?? []); } catch { /* offline */ }
  }
  useEffect(() => { load(); }, []);

  async function cancel(uuid: string, dest: string) {
    if (!confirm(`Cancel the trip to ${dest}? This clears its notification reminder.`)) return;
    await fetch(`/api/trips/${uuid}/cancel`, { method: 'POST' });
    await load();
  }

  return (
    <Page
      eyebrow="Records"
      title="Foreign travel"
      actions={<button className="btn btn-primary" type="button" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Close' : '+ Plan a trip'}</button>}
    >
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Record foreign travel here as soon as it’s booked. You must notify the police <b>at least 7 days
          before you travel</b> — and unlike other duties, this one <b>can’t be done after the fact</b>. Capture
          what you hold: each <b>leg</b> (point of arrival in every country), where you’re <b>staying</b>, and
          anyone <b>travelling with you — flag any child under 18</b>. Each trip raises a deadline on your
          dashboard; the reminder appears well ahead (from ~3 weeks out).
        </p>
      </section>

      {showAdd && <AddForm onDone={() => { setShowAdd(false); load(); }} />}

      <div className="card">
        <div className="panel-head"><h3>Trips</h3><span className="hint">{trips.length}</span></div>
        <div className="ledger">
          {trips.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No trips recorded.</div>}
          {trips.map((t) => {
            const d = daysUntil(t.departure_at);
            const upcoming = t.status === 'planned' && d >= 0;
            const isOpen = open === t.uuid;
            return (
              <div key={t.uuid}>
                <div className="ledger-row">
                  <span className={`badge badge-${t.status === 'cancelled' ? 'mute' : upcoming ? (d <= 7 ? 'warn' : 'ok') : 'mute'}`}>
                    {t.status === 'cancelled' ? 'CANCELLED' : upcoming ? (d === 0 ? 'TODAY' : `IN ${d}D`) : 'PAST'}
                  </span>
                  <div className="ledger-main">
                    <div className="ledger-text">
                      <strong>{t.destination}</strong>
                      {t.carrier ? <span className="muted"> · {t.carrier}</span> : null}
                      {t.reference ? <span className="mono muted3"> · {t.reference}</span> : null}
                      {t.minor_count > 0 ? <span className="badge badge-warn" style={{ marginLeft: 8 }}>{t.minor_count} MINOR{t.minor_count === 1 ? '' : 'S'}</span> : null}
                    </div>
                    <div className="ledger-time">
                      departs {fmt(t.departure_at)}{t.return_at ? ` · returns ${fmt(t.return_at)}` : ''}
                      {upcoming ? ` · notify by ${new Date(new Date(t.departure_at).getTime() - 7 * 86400000).toISOString().slice(0, 10)}` : ''}
                      {(t.segment_count || t.accommodation_count || t.companion_count)
                        ? ` · ${t.segment_count} leg${t.segment_count === 1 ? '' : 's'}, ${t.accommodation_count} stay${t.accommodation_count === 1 ? '' : 's'}, ${t.companion_count} companion${t.companion_count === 1 ? '' : 's'}`
                        : ' · no detail yet'}
                    </div>
                  </div>
                  <button className="btn btn-ghost" type="button" style={{ alignSelf: 'center' }} onClick={() => setOpen(isOpen ? null : t.uuid)}>{isOpen ? 'Hide' : 'Details'}</button>
                  {t.status === 'planned' && (
                    <button className="btn btn-ghost" type="button" style={{ alignSelf: 'center' }} onClick={() => cancel(t.uuid, t.destination)}>Cancel</button>
                  )}
                  <Attachments entityType="trip" entityUuid={t.uuid} />
                </div>
                {isOpen && <TripDetailPanel uuid={t.uuid} onChange={load} />}
              </div>
            );
          })}
        </div>
        <div className="panel-foot">Recording the notification (on the dashboard or Reminders) is what clears the deadline — and it’s anchored.</div>
      </div>
    </Page>
  );
}

function TripDetailPanel({ uuid, onChange }: { uuid: string; onChange: () => void }) {
  const [t, setT] = useState<TripDetail | null>(null);
  const [add, setAdd] = useState<'' | 'segment' | 'accommodation' | 'companion'>('');

  async function load() { try { setT((await fetch(`/api/trips/${uuid}`).then((r) => r.json())).trip ?? null); } catch { /* */ } }
  useEffect(() => { load(); }, [uuid]);
  if (!t) return null;

  async function post(path: string, body: unknown) {
    const r = await fetch(`/api/trips/${uuid}/${path}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    if (r.ok) { setAdd(''); await load(); onChange(); }
    return r.ok;
  }

  return (
    <div style={{ padding: '4px 16px 16px', background: 'var(--surface2, rgba(0,0,0,0.02))', borderBottom: '1px solid var(--border)' }}>
      <DetailList title="Legs (point of arrival in each country)" empty="No legs recorded.">
        {t.segments.map((s) => (
          <li key={s.uuid}>
            <strong>Leg {s.seq}</strong>: {[s.to_country, s.to_point].filter(Boolean).join(' — ') || '—'}
            <span className="muted"> {[s.from_place && `from ${s.from_place}`, s.carrier, s.reference, s.travel_at && fmt(s.travel_at)].filter(Boolean).join(' · ')}</span>
          </li>
        ))}
      </DetailList>
      <DetailList title="Accommodation" empty="No accommodation recorded.">
        {t.accommodation.map((a) => (
          <li key={a.uuid}>{[a.name, a.address].filter(Boolean).join(' · ') || '—'}<span className="muted"> {[a.from_date, a.to_date].filter(Boolean).join(' → ')}</span></li>
        ))}
      </DetailList>
      <DetailList title="Travelling companions" empty="Travelling alone / none recorded.">
        {t.companions.map((c) => (
          <li key={c.uuid}>{c.name}{c.is_minor ? <span className="badge badge-warn" style={{ marginLeft: 6 }}>MINOR{c.dob ? ` · b. ${c.dob}` : ''}</span> : c.relationship ? <span className="muted"> ({c.relationship})</span> : null}</li>
        ))}
      </DetailList>

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" type="button" onClick={() => setAdd(add === 'segment' ? '' : 'segment')}>+ Leg</button>
        <button className="btn btn-ghost" type="button" onClick={() => setAdd(add === 'accommodation' ? '' : 'accommodation')}>+ Accommodation</button>
        <button className="btn btn-ghost" type="button" onClick={() => setAdd(add === 'companion' ? '' : 'companion')}>+ Companion</button>
      </div>
      {add === 'segment' && <SegmentForm onSubmit={(b) => post('segments', b)} />}
      {add === 'accommodation' && <AccommodationForm onSubmit={(b) => post('accommodation', b)} />}
      {add === 'companion' && <CompanionForm onSubmit={(b) => post('companions', b)} />}
    </div>
  );
}

function DetailList({ title, empty, children }: { title: string; empty: string; children: ReactNode }) {
  const arr = Array.isArray(children) ? children : [children];
  const has = arr.some((c) => c);
  return (
    <div style={{ marginTop: 10 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{title}</div>
      {has ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>{children}</ul> : <div className="muted3" style={{ fontSize: 12.5 }}>{empty}</div>}
    </div>
  );
}

const rowStyle = { display: 'flex', gap: 8, flexWrap: 'wrap' as const, marginTop: 8 };

function SegmentForm({ onSubmit }: { onSubmit: (b: unknown) => Promise<boolean> }) {
  const [f, setF] = useState({ to_country: '', to_point: '', from_place: '', carrier: '', reference: '', date: '' });
  return (
    <form className="card card-pad" style={{ marginTop: 8 }} onSubmit={async (e) => { e.preventDefault(); if (!f.to_country.trim()) return; await onSubmit({ ...f, ...(f.date ? { travel_at: `${f.date}T09:00:00.000Z` } : {}) }); }}>
      <div style={rowStyle}>
        <label className="fld" style={{ flex: '1 1 130px' }}><span>Country of arrival</span><input value={f.to_country} onChange={(e) => setF({ ...f, to_country: e.target.value })} placeholder="Spain" /></label>
        <label className="fld" style={{ flex: '1 1 130px' }}><span>Point of arrival</span><input value={f.to_point} onChange={(e) => setF({ ...f, to_point: e.target.value })} placeholder="Alicante (ALC)" /></label>
        <label className="fld" style={{ flex: '1 1 130px' }}><span>Departing from</span><input value={f.from_place} onChange={(e) => setF({ ...f, from_place: e.target.value })} placeholder="Manchester (MAN)" /></label>
      </div>
      <div style={rowStyle}>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>Carrier</span><input value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} placeholder="Jet2" /></label>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>Flight / ref</span><input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="LS1751" /></label>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>Date</span><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></label>
      </div>
      <button className="btn btn-primary" type="submit" style={{ marginTop: 8 }}>Add leg</button>
    </form>
  );
}

function AccommodationForm({ onSubmit }: { onSubmit: (b: unknown) => Promise<boolean> }) {
  const [f, setF] = useState({ name: '', address: '', from_date: '', to_date: '' });
  return (
    <form className="card card-pad" style={{ marginTop: 8 }} onSubmit={async (e) => { e.preventDefault(); if (!f.name.trim() && !f.address.trim()) return; await onSubmit(f); }}>
      <div style={rowStyle}>
        <label className="fld" style={{ flex: '1 1 150px' }}><span>Name / host</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Hotel Sol" /></label>
        <label className="fld" style={{ flex: '2 1 220px' }}><span>Address</span><input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Av. del Mar 1, Alicante" /></label>
      </div>
      <div style={rowStyle}>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>From</span><input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} /></label>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>To</span><input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} /></label>
      </div>
      <button className="btn btn-primary" type="submit" style={{ marginTop: 8 }}>Add accommodation</button>
    </form>
  );
}

function CompanionForm({ onSubmit }: { onSubmit: (b: unknown) => Promise<boolean> }) {
  const [f, setF] = useState({ name: '', relationship: '', is_minor: false, dob: '' });
  return (
    <form className="card card-pad" style={{ marginTop: 8 }} onSubmit={async (e) => { e.preventDefault(); if (!f.name.trim()) return; await onSubmit(f); }}>
      <div style={rowStyle}>
        <label className="fld" style={{ flex: '2 1 160px' }}><span>Name</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" /></label>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>Relationship</span><input value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} placeholder="partner / son" /></label>
        {f.is_minor && <label className="fld" style={{ flex: '1 1 120px' }}><span>Date of birth</span><input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} /></label>}
      </div>
      <label className="fld" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <input type="checkbox" checked={f.is_minor} onChange={(e) => setF({ ...f, is_minor: e.target.checked })} style={{ width: 'auto', margin: 0 }} />
        <span style={{ margin: 0 }}>This companion is a <b>child under 18</b> (notifiable particular)</span>
      </label>
      <button className="btn btn-primary" type="submit" style={{ marginTop: 8 }}>Add companion</button>
    </form>
  );
}

function AddForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ destination: '', date: '', time: '09:00', return_date: '', carrier: '', reference: '' });
  const [companions, setCompanions] = useState<{ name: string; relationship: string; is_minor: boolean; dob: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setComp(i: number, patch: Partial<{ name: string; relationship: string; is_minor: boolean; dob: string }>) {
    setCompanions((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!f.destination.trim()) { setErr('Where are you travelling to?'); return; }
    if (!f.date) { setErr('Enter the departure date.'); return; }
    setBusy(true);
    try {
      const departure_at = `${f.date}T${f.time || '09:00'}:00.000Z`;
      const res = await fetch('/api/trips', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ destination: f.destination, departure_at,
          ...(f.return_date ? { return_at: `${f.return_date}T09:00:00.000Z` } : {}),
          ...(f.carrier ? { carrier: f.carrier } : {}), ...(f.reference ? { reference: f.reference } : {}),
          companions: companions.filter((c) => c.name.trim()) }),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed'); return; }
      onDone();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }

  return (
    <form className="card card-pad" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15 }}>Plan a trip</h3>
      <label className="fld"><span>Destination</span>
        <input value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} placeholder="e.g. Spain (Alicante)" />
      </label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Departure date</span>
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </label>
        <label className="fld" style={{ flex: '1 1 110px' }}><span>Time</span>
          <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} />
        </label>
        <label className="fld" style={{ flex: '1 1 140px' }}><span>Return (optional)</span>
          <input type="date" value={f.return_date} onChange={(e) => setF({ ...f, return_date: e.target.value })} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label className="fld" style={{ flex: 1 }}><span>Airline / carrier (optional)</span>
          <input value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} placeholder="Jet2" />
        </label>
        <label className="fld" style={{ flex: 1 }}><span>Flight / booking ref (optional)</span>
          <input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="LS1751" />
        </label>
      </div>

      <div style={{ marginTop: 6 }}>
        <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Travelling companions (flag any child under 18)</div>
        {companions.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
            <label className="fld" style={{ flex: '2 1 140px' }}><span>Name</span><input value={c.name} onChange={(e) => setComp(i, { name: e.target.value })} placeholder="Full name" /></label>
            <label className="fld" style={{ flex: '1 1 110px' }}><span>Relationship</span><input value={c.relationship} onChange={(e) => setComp(i, { relationship: e.target.value })} placeholder="partner" /></label>
            {c.is_minor && <label className="fld" style={{ flex: '1 1 120px' }}><span>DOB</span><input type="date" value={c.dob} onChange={(e) => setComp(i, { dob: e.target.value })} /></label>}
            <label className="fld" style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
              <input type="checkbox" checked={c.is_minor} onChange={(e) => setComp(i, { is_minor: e.target.checked })} style={{ width: 'auto', margin: 0 }} /><span style={{ margin: 0, fontSize: 12.5 }}>Minor</span>
            </label>
            <button className="btn btn-ghost" type="button" onClick={() => setCompanions((cs) => cs.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
        <button className="btn btn-ghost" type="button" onClick={() => setCompanions((cs) => [...cs, { name: '', relationship: '', is_minor: false, dob: '' }])}>+ Add companion</button>
        <p className="muted3" style={{ fontSize: 12, margin: '6px 0 0' }}>Legs and accommodation can be added from <b>Details</b> after saving — capture them as you firm up the booking.</p>
      </div>

      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{err}</div>}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 6 }}>{busy ? 'Saving…' : 'Save trip'}</button>
    </form>
  );
}
